import React, { useCallback, useMemo, useState } from 'react';
import Icon from 'carbon-react/lib/components/icon';

import { FormattedMessage } from '@futrli/localisation';
import { useSidebarContext } from '@futrli/shared-services/contexts/sidebar-context/useSidebarContext';

import { openPdfForViewing, downloadPdfFile } from '../../../../utils/pdf-actions';
import { ExportedPdfRowItem } from '../types';

interface UseExportedPdfActionItemsArgs {
  item: ExportedPdfRowItem;
  onDelete: () => void;
}

/**
 * Builds the list of action menu items for an exported PDF row.
 * Returns different actions depending on the item type (exported vs dispatched).
 *
 * @param args.item - The exported or dispatched report row item.
 * @param args.onDelete - Callback to trigger the delete dialog.
 * @returns An array of action items with labels, click handlers, and disabled state.
 */
export const useExportedPdfActionItems = ({ item, onDelete }: UseExportedPdfActionItemsArgs) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { emailPdfReport } = useSidebarContext();

  const handleViewPdf = useCallback(async () => {
    if (!item.download_link || !item.name) {
      return;
    }
    await openPdfForViewing(item.download_link);
  }, [item.download_link, item.name]);

  const handleDownloadPdf = useCallback(async () => {
    if (!item.download_link || !item.name) {
      return;
    }
    try {
      setIsDownloading(true);
      await downloadPdfFile(item.download_link, `${item.name}.pdf`);
    } finally {
      setIsDownloading(false);
    }
  }, [item.download_link, item.name]);

  const items = useMemo(() => {
    if (item.type === 'exported') {
      return [
        {
          label: <FormattedMessage id="predict.actions.email_pdf" defaultMessage="Email PDF report" />,
          onClick: () => {
            emailPdfReport.open({ reportName: item.name, exportedReportId: item.id });
          }
        },
        {
          label: <FormattedMessage id="predict.actions.view_pdf" defaultMessage="View PDF" />,
          onClick: handleViewPdf
        },
        {
          label: <FormattedMessage id="predict.actions.download_pdf" defaultMessage="Download PDF" />,
          onClick: handleDownloadPdf,
          disabled: isDownloading
        },
        {
          label: (
            <>
              <Icon type="delete" fontSize="small" />
              <FormattedMessage id="common.delete" defaultMessage="Delete" />
            </>
          ),
          onClick: onDelete,
          variant: 'danger' as const
        }
      ];
    }

    // dispatched row
    return [
      {
        label: <FormattedMessage id="predict.actions.view_pdf" defaultMessage="View PDF" />,
        onClick: handleViewPdf
      },
      {
        label: <FormattedMessage id="predict.actions.download_pdf" defaultMessage="Download PDF" />,
        onClick: handleDownloadPdf,
        disabled: isDownloading
      }
    ];
  }, [item.type, item.id, item.name, handleViewPdf, handleDownloadPdf, isDownloading, emailPdfReport, onDelete]);

  return items;
};
